/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import {
  MantineReactTable,
  MRT_RowData,
  MRT_TableOptions,
  useMantineReactTable,
} from 'mantine-react-table';
import { MRT_Localization_EN } from 'mantine-react-table/locales/en/index.cjs';
import { MRT_Localization_FR } from 'mantine-react-table/locales/fr/index.cjs';
import { useTranslation } from 'react-i18next';

import SurfacePanel from '@ui-kit/organisms/SurfacePanel';
import { TableProps } from './types';
import * as classes from './styles.css';

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */

export function useTable<T extends {}>({ ...props }: MRT_TableOptions<T>) {
  const { i18n, t } = useTranslation();
  const locale = i18n.language;
  let localization;
  switch (locale) {
    case 'fr':
      localization = MRT_Localization_FR;
      break;
    case 'en':
    default:
      localization = MRT_Localization_EN;
      break;
  }

  localization = {
    ...localization,
    move: t('Common:cta.move'),
  };

  const table = useMantineReactTable({
    localization,
    mantineTableProps: {
      striped: false,
    },
    mantineSkeletonProps: {
      height: 14,
      radius: 'sm',
    },
    ...props,
  });
  return table;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function Table<T extends MRT_RowData>({
  onClickRow,
  unstyledShell = false,
  ...tableProps
}: TableProps<T>) {
  const { table, data = [], columns = [], ...otherProps } = tableProps;
  const content = table ? (
    <MantineReactTable<T> table={table} />
  ) : (
    <MantineReactTable<T> columns={columns} data={data} {...otherProps} />
  );

  if (unstyledShell) {
    return content;
  }

  return <SurfacePanel className={classes.shell}>{content}</SurfacePanel>;
}

export default Table;
